import { DownloadIcon } from "lucide-react";
import type { IsFullWidthRowParams, RowHeightParams } from "ag-grid-community";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AuditEventDrawer } from "./AuditLog/AuditEventDrawer";
import { AuditFilterPanel } from "./AuditLog/AuditFilterPanel";
import {
  AuditFilterStoreProvider,
  useAuditFilterStore,
} from "./AuditLog/audit-filter-store-context";
import { AuditGroupHeaderCell } from "./AuditLog/AuditGroupHeaderCell";
import { AuditLogPagination } from "./AuditLog/AuditLogPagination";
import {
  AuditTableRow,
  buildAuditTableRows,
  computeAuditTableHeight,
  isAuditEventTableRow,
} from "./AuditLog/auditTableUtils";
import { useAuditFilterURLSync } from "./AuditLog/useAuditFilterURLSync";
import { useAuditLogColumns } from "./AuditLog/useAuditLogColumns";
import { SectionShell } from "./SectionShell";

import { Button } from "@/components/ui/button";
import RefreshButton from "@/components/ui/refresh-button";
import SearchBar from "@/components/ui/SearchBar";
import { DataTable } from "@/components/ui/ag-grid-table";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import {
  downloadAuditCsv,
  IdentityAuditEvent,
  useIdentityAuditEventsPage,
} from "@/queries/identity/useIdentityAuditEvents";
import { displayToast } from "@/utils/toast";

interface Props {
  organizationId: number;
}

const AuditLogSectionContent = observer(({ organizationId }: Props) => {
  const filterStore = useAuditFilterStore();
  useAuditFilterURLSync(filterStore);

  const userToken = useAuth()?.userToken;
  const [isExporting, setIsExporting] = useState(false);
  const [selected, setSelected] = useState<IdentityAuditEvent | null>(null);
  const [localSearch, setLocalSearch] = useState(filterStore.filters.q ?? "");
  const { columns } = useAuditLogColumns();

  const {
    events,
    totalForFilter,
    page,
    pageSize,
    totalPages,
    hasNext,
    hasPrev,
    setPageIndex,
    setPageSize,
    isLoading,
    isFetching,
    refetch,
  } = useIdentityAuditEventsPage(organizationId, filterStore.filters);

  const tableRows = useMemo(() => buildAuditTableRows(events), [events]);
  const tableHeight = useMemo(() => computeAuditTableHeight(tableRows), [tableRows]);

  useEffect(() => {
    setLocalSearch(filterStore.filters.q ?? "");
  }, [filterStore.filters.q]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = localSearch.trim();
      if (next !== (filterStore.filters.q ?? "")) {
        filterStore.setQuery(next || null);
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [filterStore, localSearch]);

  const buildDescription = (): string => {
    if (isLoading) {
      return "Loading events…";
    }
    if (totalForFilter > 0) {
      const start = (page - 1) * pageSize + 1;
      const end = Math.min(page * pageSize, totalForFilter);
      return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${totalForFilter.toLocaleString()} events matching the current filters.`;
    }
    if (filterStore.isAnyActive) {
      return "No events match the current filters.";
    }
    return "No identity events yet. Provision a user via SSO or SCIM to populate this log.";
  };

  const handleExport = async () => {
    if (!userToken) {
      displayToast("You must be signed in to export.", ToastType.error);
      return;
    }
    setIsExporting(true);
    try {
      await downloadAuditCsv(organizationId, filterStore.filters, userToken);
    } catch (err) {
      displayToast(err instanceof Error ? err.message : "CSV export failed.", ToastType.error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRowClick = useCallback((row: AuditTableRow) => {
    if (isAuditEventTableRow(row)) {
      setSelected(row);
    }
  }, []);

  const isFullWidthRow = useCallback(
    (params: IsFullWidthRowParams<AuditTableRow>) => params.rowNode.data?.rowType === "groupHeader",
    [],
  );

  const getRowHeight = useCallback((params: RowHeightParams<AuditTableRow>) => {
    if (params.node.data?.rowType === "groupHeader") {
      return 36;
    }
    return 48;
  }, []);

  const emptyStateMessage = filterStore.isAnyActive
    ? "No events match the current filters."
    : "No identity events yet. Provision a user via SSO or SCIM to populate this log.";

  return (
    <SectionShell
      title="Audit log"
      description={buildDescription()}
      actions={
        <div className="flex items-center gap-1">
          <RefreshButton
            size="sm"
            tooltip="Refresh audit log"
            onClick={() => refetch()}
            isRefreshing={isFetching && !isLoading}
            disabled={isLoading}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting || totalForFilter === 0}
          >
            <DownloadIcon className="mr-1.5 h-3.5 w-3.5" />
            {isExporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      }
    >
      <DataTable<AuditTableRow>
        data={tableRows}
        columns={columns}
        isLoading={isLoading}
        searchBarAddon={
          <SearchBar
            value={localSearch}
            onChange={setLocalSearch}
            placeholderPhrasesArray={["Search event type, source, or payload"]}
            className="min-w-[300px]"
            size="md"
            clearable
          />
        }
        filterContent={<AuditFilterPanel />}
        activeFilterCount={filterStore.activeFilterCount}
        onRowClick={handleRowClick}
        hoverable
        enableSorting={false}
        rowHeight={48}
        height={tableHeight}
        emptyStateMessage={emptyStateMessage}
        getRowId={params => String(params.data.id)}
        isFullWidthRow={isFullWidthRow}
        fullWidthCellRenderer={AuditGroupHeaderCell}
        getRowHeight={getRowHeight}
        className="[&_.ag-cell-value]:justify-start [&_.ag-header-cell-label]:justify-start [&_.ag-header-cell-text]:text-left"
      />

      <AuditLogPagination
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        hasNext={hasNext}
        hasPrev={hasPrev}
        isLoading={isLoading || isFetching}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
      />

      <AuditEventDrawer event={selected} onClose={() => setSelected(null)} />
    </SectionShell>
  );
});

/**
 * Enterprise identity audit log: MobX filter store mirrored to URL params,
 * DataTable with day-group headers, standard pagination, and CSV export.
 */
export const AuditLogSection = ({ organizationId }: Props) => (
  <AuditFilterStoreProvider>
    <AuditLogSectionContent organizationId={organizationId} />
  </AuditFilterStoreProvider>
);
